import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg, isManagerRole } from "@/lib/api";
import { isNetErr, sendPrint, setCacheScope } from "@/lib/mobile";
import { ring, unlockAudio } from "@/lib/push";
import { useAuth } from "@/context/AuthContext";

const Ctx = createContext(null);
const read = (k, d) => { try { return JSON.parse(localStorage.getItem(k)) ?? d; } catch { return d; } };

function useStored(key, init) {
  const [value, setValue] = useState(() => read(key, init));
  const ref = useRef(value);
  const write = useCallback((next) => {
    const v = typeof next === "function" ? next(ref.current) : next;
    ref.current = v;
    localStorage.setItem(key, JSON.stringify(v));
    setValue(v);
  }, [key]);
  return [value, write, ref];
}

function useNotifications(user) {
  const key = `m_notif_seen_${user.id}`;
  const manager = isManagerRole(user);
  const [items, setItems] = useState([]);
  const [seen, setSeen] = useState(() => localStorage.getItem(key) || "");
  const known = useRef(null);
  const nav = useNavigate();
  useEffect(() => {
    const alert = (n) => {
      if (n.kind === "liberacao") {
        if (!manager) return;
        ring("liberacao");
        toast.warning("Novo pedido de liberação", { description: n.text, duration: 8000, action: { label: "Ver", onClick: () => nav("/m/liberacao") } });
        return;
      }
      ring(n.kind);
      const show = n.kind === "aprovado" ? toast.success : toast.error;
      show(n.title, { description: n.text, duration: 10000, action: { label: "Abrir", onClick: () => nav(n.link) } });
    };
    const load = () => api.get("/mobile/notifications").then((r) => {
      setItems(r.data);
      const watched = r.data.filter((n) => ["liberacao", "aprovado", "recusado"].includes(n.kind));
      if (known.current) watched.filter((n) => !known.current.has(n.id)).forEach(alert);
      known.current = new Set(watched.map((n) => n.id));
    }).catch(() => {});
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [manager, nav]);
  const unread = items.filter((n) => n.at && (!seen || new Date(n.at) > new Date(seen))).length;
  const markSeen = useCallback(() => { const v = new Date().toISOString(); localStorage.setItem(key, v); setSeen(v); }, [key]);
  return { notifs: items, unread, markSeen, pendingApprovals: items.filter((n) => n.kind === "liberacao").length };
}

export function MobileProvider({ children }) {
  const { session } = useAuth();
  const t = session.tenant.id;
  setCacheScope(t);
  const [queue, setQueue, qRef] = useStored(`m_queue_${t}`, []);
  const [carts, setCarts] = useStored(`m_carts_${t}`, {});
  const [idMap, setIdMap] = useStored(`m_idmap_${t}`, {});
  const [online, setOnline] = useState(navigator.onLine);
  const [drawer, setDrawer] = useState(false);
  const notif = useNotifications(session.user);
  const busy = useRef(false);
  useEffect(() => {
    window.addEventListener("pointerdown", unlockAudio, { once: true });
    return () => window.removeEventListener("pointerdown", unlockAudio);
  }, []);

  const sendEntry = useCallback(async (e) => {
    if (e.type === "create") {
      const { data } = await api.post("/comandas", e.payload);
      setIdMap((m) => ({ ...m, [e.temp_id]: data.id }));
      setQueue((q) => q.map((x) => (x.comanda_id === e.temp_id ? { ...x, comanda_id: data.id } : x)));
      setCarts((all) => { if (!all[e.temp_id]) return all; const { [e.temp_id]: moved, ...rest } = all; return { ...rest, [data.id]: moved }; });
      return;
    }
    const { data } = await api.post(`/comandas/${e.comanda_id}/items`, e.payload);
    const o = data.last_order;
    if (o && (o.items.length || o.sectors.length)) sendPrint("order", `Pedido ${o.number} · ${o.label}`, { order: o }).catch(() => {});
  }, [setIdMap, setQueue, setCarts]);

  const flush = useCallback(async () => {
    if (busy.current || !qRef.current.some((e) => !e.error)) return;
    busy.current = true;
    let sent = 0;
    try {
      for (const { id } of [...qRef.current]) {
        const e = qRef.current.find((x) => x.id === id);
        if (!e || e.error) continue;
        try {
          await sendEntry(e);
          setQueue((q) => q.filter((x) => x.id !== id));
          sent += 1;
          setOnline(true);
        } catch (err) {
          if (isNetErr(err)) { setOnline(false); break; }
          setQueue((q) => q.map((x) => (x.id === id ? { ...x, error: errMsg(err) } : x)));
        }
      }
    } finally {
      busy.current = false;
    }
    if (sent) toast.success(`${sent} envio(s) guardado(s) no celular foram enviados`);
  }, [qRef, sendEntry, setQueue]);

  useEffect(() => {
    const up = () => { setOnline(true); flush(); };
    const down = () => setOnline(false);
    window.addEventListener("online", up);
    window.addEventListener("offline", down);
    const timer = setInterval(flush, 15000);
    flush();
    return () => { window.removeEventListener("online", up); window.removeEventListener("offline", down); clearInterval(timer); };
  }, [flush]);

  const enqueue = useCallback((entry) => setQueue((q) => [...q, { id: `q-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, at: new Date().toISOString(), error: "", ...entry }]), [setQueue]);
  const retry = useCallback((id) => { setQueue((q) => q.map((x) => (x.id === id ? { ...x, error: "" } : x))); setTimeout(flush, 50); }, [setQueue, flush]);
  const discard = useCallback((id) => setQueue((q) => {
    const e = q.find((x) => x.id === id);
    return q.filter((x) => x.id !== id && !(e?.type === "create" && x.comanda_id === e.temp_id));
  }), [setQueue]);
  const resolveId = useCallback((id) => idMap[id] || id, [idMap]);
  const cartOf = useCallback((cid) => carts[cid] || [], [carts]);
  const setCart = useCallback((cid, fn) => setCarts((all) => ({ ...all, [cid]: typeof fn === "function" ? fn(all[cid] || []) : fn })), [setCarts]);
  const pendingFor = useCallback((cid) => queue.filter((e) => e.type === "items" && e.comanda_id === cid), [queue]);

  const value = { online, setOnline, queue, enqueue, flush, retry, discard, resolveId, cartOf, setCart, pendingFor, drawer, setDrawer, ...notif };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useMobile = () => useContext(Ctx);
